import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { startRegistration } from "@simplewebauthn/browser";
import {
  KeyIcon,
  LockIcon,
  LockOpenIcon,
  MailIcon,
  Trash2Icon,
} from "lucide-react";

import {
  MFA_METHOD_ORDER,
  type MfaMethod,
  getPasskeyUserMessage,
} from "simplydo/core/mfa";
import { Button } from "simplydo/react/ui/button";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import { useUserReload } from "utils/useUserReload";

import { EnableMfa } from "../EnableMfa";
import { MfaMethodCard } from "./MfaMethodCard";

type MfaSettingsSectionProps = {
  mfaEnforced: boolean;
  onMethodsChanged?: () => void;
};

export const MfaSettingsSection = ({
  mfaEnforced,
  onMethodsChanged,
}: MfaSettingsSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const reloadUser = useUserReload();

  const afterMethodsChanged = useCallback(() => {
    reloadUser(() => onMethodsChanged?.());
  }, [reloadUser, onMethodsChanged]);
  const emailVerified = Boolean(user?.emails?.[0]?.verified);
  const configuredMethods = (user?.mfaMethods ?? []) as MfaMethod[];
  const passkeys = user?.passkeys ?? [];
  const [enableTotpOpen, setEnableTotpOpen] = useState(false);
  const [passwordModal, setPasswordModal] = useState<MfaMethod | null>(null);
  const [password, setPassword] = useState("");
  const [passkeyName, setPasskeyName] = useState("");
  const [passkeyIdToDelete, setPasskeyIdToDelete] = useState<string | null>(
    null,
  );
  const [passkeyModal, setPasskeyModal] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [sendingVerifyEmail, setSendingVerifyEmail] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);

  const closePasswordModal = () => {
    setPasswordModal(null);
    setPassword("");
    setPasskeyIdToDelete(null);
  };

  const runWithPassword = (method: MfaMethod) => {
    if (!password) return;
    const done = () => {
      closePasswordModal();
      afterMethodsChanged();
    };
    if (method === "totp") {
      api.auth.disableOtp(
        { password },
        () => {
          toast.success(t("entry.mfa.disabledMethodSuccess.totp"));
          done();
        },
        (err) => toast.error(err.message),
      );
    } else if (method === "emailOtp") {
      api.auth.disableEmailOtpMfa(
        { password },
        () => {
          toast.success(t("entry.mfa.disabledMethodSuccess.emailOtp"));
          done();
        },
        (err) => toast.error(err.message),
      );
    }
  };

  const enableEmailOtp = (options?: {
    loadingToastId?: string;
    onSettled?: () => void;
  }) => {
    api.auth.enableEmailOtpMfa(
      () => {
        options?.onSettled?.();
        toast.success(t("entry.mfa.statusMessageSuccess"), {
          id: options?.loadingToastId,
        });
        afterMethodsChanged();
      },
      (err) => {
        options?.onSettled?.();
        toast.error(err.message, { id: options?.loadingToastId });
      },
    );
  };

  const registerPasskey = async () => {
    if (!passkeyName.trim() || passkeyLoading) return;
    setPasskeyLoading(true);
    try {
      const options = await new Promise<Record<string, unknown>>(
        (resolve, reject) => {
          api.auth.passkeyRegisterOptions(resolve, reject);
        },
      );
      const credential = await startRegistration({
        optionsJSON: options as unknown as Parameters<
          typeof startRegistration
        >[0]["optionsJSON"],
      });
      api.auth.passkeyRegisterVerify(
        {
          credential: credential as unknown as Record<string, never>,
          name: passkeyName.trim(),
        },
        () => {
          toast.success(t("entry.mfa.passkeyRegistered"));
          closePasskeyModal();
          afterMethodsChanged();
        },
        (err) => {
          setPasskeyLoading(false);
          toast.error(err.message);
        },
      );
    } catch (err) {
      setPasskeyLoading(false);
      const message = getPasskeyUserMessage(err, t);
      if (message) {
        toast.error(message);
      }
    }
  };

  const closePasskeyModal = () => {
    setPasskeyModal(false);
    setPasskeyName("");
    setPasskeyLoading(false);
  };

  const deletePasskey = (passkeyId: string) => {
    if (!password) return;
    api.auth.deletePasskey(
      passkeyId,
      { password },
      () => {
        toast.success(t("entry.mfa.passkeyRemoved"));
        closePasswordModal();
        afterMethodsChanged();
      },
      (err) => toast.error(err.message),
    );
  };

  const checkAgainAndEnable = () => {
    setSendingVerifyEmail(true);
    const loadingId = toast.loading(
      t("entry.verifyEmail.resend.statusMessageLoading"),
    );
    api.users.me(
      "",
      (me) => {
        api.setQueryData(api.users.me, [], me);
        dispatch(actions.user.receiveUser(me));
        if (me.emails?.[0]?.verified) {
          enableEmailOtp({
            loadingToastId: loadingId,
            onSettled: () => setSendingVerifyEmail(false),
          });
          return;
        }
        setSendingVerifyEmail(false);
        toast.error(t("entry.mfa.emailOtpRequiresVerifiedEmail"), {
          id: loadingId,
        });
      },
      (err) => {
        setSendingVerifyEmail(false);
        toast.error(err.message, { id: loadingId });
      },
    );
  };

  const sendVerificationEmail = () => {
    setSendingVerifyEmail(true);
    const loadingId = toast.loading(
      t("entry.verifyEmail.resend.statusMessageLoading"),
    );
    api.auth.requestEmailVerification(
      "",
      () => {
        setSendingVerifyEmail(false);
        setVerificationEmailSent(true);
        toast.success(t("entry.verifyEmail.resend.statusMessageSuccess"), {
          id: loadingId,
        });
      },
      (err) => {
        if (err.message === "This email is already verified") {
          enableEmailOtp({
            loadingToastId: loadingId,
            onSettled: () => setSendingVerifyEmail(false),
          });
          return;
        }
        setSendingVerifyEmail(false);
        toast.error(err.message, { id: loadingId });
      },
    );
  };

  const handleVerifyEmailAction = () => {
    if (verificationEmailSent) {
      checkAgainAndEnable();
      return;
    }
    sendVerificationEmail();
  };

  const openEnable = (method: MfaMethod) => {
    if (method === "totp") {
      setEnableTotpOpen(true);
      return;
    }
    if (method === "emailOtp") {
      enableEmailOtp();
      return;
    }
    if (method === "passkey") {
      setPasskeyModal(true);
    }
  };

  const openDisable = (method: MfaMethod, passkeyId?: string) => {
    if (method === "passkey" && passkeyId) {
      setPasskeyIdToDelete(passkeyId);
      setPasswordModal("passkey");
      return;
    }
    setPasswordModal(method);
  };

  return (
    <>
      <div>
        {MFA_METHOD_ORDER.map((method) => {
          const isConfigured =
            method === "passkey"
              ? passkeys.length > 0
              : configuredMethods.includes(method);
          const emailSetupBlocked = method === "emailOtp" && !emailVerified;

          if (method === "passkey" && isConfigured) {
            const passkey = passkeys[0];
            if (!passkey) return null;
            return (
              <MfaMethodCard
                key="passkey"
                method="passkey"
                configured
                actions={
                  <Button onClick={() => openDisable("passkey", passkey.id)}>
                    <Icon icon={Trash2Icon} />
                    {t("entry.mfa.removePasskey")}
                  </Button>
                }
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    background: "rgba(0,0,0,0.03)",
                    borderRadius: 4,
                  }}
                >
                  <Icon icon={KeyIcon} style={{ margin: 0, opacity: 0.6 }} />
                  <span style={{ fontWeight: 500 }}>{passkey.name}</span>
                </div>
              </MfaMethodCard>
            );
          }

          if (isConfigured) {
            return (
              <MfaMethodCard
                key={method}
                method={method}
                configured
                actions={
                  <Button onClick={() => openDisable(method)}>
                    <Icon icon={LockOpenIcon} />
                    {t(`entry.mfa.disableMethod.${method}`)}
                  </Button>
                }
              />
            );
          }

          return (
            <MfaMethodCard
              key={method}
              method={method}
              actions={
                emailSetupBlocked ? (
                  <Button
                    loading={sendingVerifyEmail}
                    onClick={() => handleVerifyEmailAction()}
                    color="primary"
                  >
                    <Icon icon={MailIcon} />
                    {verificationEmailSent
                      ? t("entry.mfa.checkAgainAndEnable")
                      : t("entry.mfa.verifyEmailAction")}
                  </Button>
                ) : (
                  <Button onClick={() => openEnable(method)} color="primary">
                    <Icon icon={method === "passkey" ? KeyIcon : LockIcon} />
                    {t(`entry.mfa.enableMethod.${method}`)}
                  </Button>
                )
              }
            >
              {emailSetupBlocked ? (
                <p style={{ margin: 0, color: "rgba(0,0,0,0.55)" }}>
                  {t("entry.mfa.emailOtpRequiresVerifiedEmail")}
                </p>
              ) : null}
            </MfaMethodCard>
          );
        })}
      </div>

      <EnableMfa
        open={enableTotpOpen}
        onClose={() => setEnableTotpOpen(false)}
        onEnableMfa={() => {
          afterMethodsChanged();
        }}
      />

      <Modal
        open={passkeyModal}
        size="small"
        onClose={() => closePasskeyModal()}
      >
        <ModalHeader>{t("entry.mfa.enableMethod.passkey")}</ModalHeader>
        <ModalContent>
          <p style={{ marginTop: 0, marginBottom: 16 }}>
            {t("entry.mfa.passkeySetupInfo")}
          </p>
          <FormField>
            <FormLabel htmlFor="passkey-name-input">
              {t("entry.mfa.passkeyName")}
            </FormLabel>
            <Input
              id="passkey-name-input"
              fluid
              autoFocus
              value={passkeyName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPasskeyName(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter" && passkeyName.trim()) {
                  registerPasskey();
                }
              }}
              placeholder={t("entry.mfa.passkeyNamePlaceholder")}
            />
          </FormField>
        </ModalContent>
        <ModalActions>
          <Button disabled={passkeyLoading} onClick={() => closePasskeyModal()}>
            {t("generic.cancel")}
          </Button>
          <Button
            loading={passkeyLoading}
            disabled={!passkeyName.trim()}
            onClick={() => registerPasskey()}
            color="primary"
          >
            <Icon icon={KeyIcon} />
            {t("entry.mfa.enableMethod.passkey")}
          </Button>
        </ModalActions>
      </Modal>

      <Modal open={passwordModal !== null}>
        <ModalHeader>
          {passwordModal === "passkey"
            ? t("entry.mfa.removePasskey")
            : passwordModal
              ? t(`entry.mfa.disableMethod.${passwordModal}`)
              : t("entry.mfa.disableAction")}
        </ModalHeader>
        <ModalContent>
          <p>{t("entry.mfa.requirePassword")}</p>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
          {mfaEnforced && passwordModal ? (
            <Message color="blue" style={{ marginTop: 10 }}>
              {t(`entry.mfa.disabledWhenEnforcedMessage.${passwordModal}`, {
                orgName: user?.ownerOrganisation?.name,
              })}
            </Message>
          ) : null}
        </ModalContent>
        <ModalActions>
          <Button onClick={() => closePasswordModal()}>
            {t("generic.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (passwordModal === "passkey" && passkeyIdToDelete) {
                deletePasskey(passkeyIdToDelete);
              } else if (passwordModal) {
                runWithPassword(passwordModal);
              }
            }}
            color="primary"
          >
            {passwordModal === "passkey"
              ? t("entry.mfa.removePasskey")
              : passwordModal
                ? t(`entry.mfa.disableMethod.${passwordModal}`)
                : t("generic.verify")}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};
